import { gsap } from "gsap";
import {
  buildHitAreaAnimationArray,
  HIT_AREA_MODAL_TIMELINE_DURATION,
  roundTimelineTime,
  roundToInt,
} from "./utils";

export const getHitAreaModalPreviewElementId = (hitAreaId) =>
  `hit-area-modal-${String(hitAreaId || "").replace(/[^a-zA-Z0-9_-]/g, "-")}`;

export const buildHitAreaModalAnimationObject = (hitAreas = []) => {
  const animationObject = {};

  hitAreas.forEach((hitArea) => {
    if (!hitArea?.id || hitArea.hideBoolean) return;

    const keyframes = [...(hitArea.keyframes || [])]
      .filter((kf) => typeof kf.time === "number" && Number.isFinite(kf.time))
      .sort((first, second) => first.time - second.time);

    if (!keyframes.length) return;

    animationObject[hitArea.id] = {
      animation: buildHitAreaAnimationArray(hitArea, keyframes),
    };
  });

  return animationObject;
};

const toGsapEase = (ease) => {
  if (!ease || ease === "NONE" || ease === "linear") return "none";
  return ease;
};

const toDisplayTweenVars = (entry, widthScale, heightScale) => ({
  left: roundToInt((entry.x || 0) * widthScale),
  top: roundToInt((entry.y || 0) * heightScale),
  width: Math.max(5, roundToInt((entry.width || 0) * widthScale)),
  height: Math.max(5, roundToInt((entry.height || 0) * heightScale)),
  opacity: entry.opacity ?? 1,
});

export const resetHitAreaModalPreviewElements = (hitAreas = []) => {
  hitAreas.forEach((hitArea) => {
    if (!hitArea?.id) return;
    const element = document.getElementById(
      getHitAreaModalPreviewElementId(hitArea.id)
    );
    if (!element) return;
    gsap.killTweensOf(element);
    gsap.set(element, { clearProps: "transform,left,top,width,height,opacity" });
  });
};

export const createHitAreaModalPlaybackTimeline = ({
  hitAreas = [],
  imageWidth = 1,
  imageHeight = 1,
  stageWidth = 1,
  stageHeight = 1,
  startTime = 0,
  totalDuration = HIT_AREA_MODAL_TIMELINE_DURATION,
  autoplay = true,
  onTimeUpdate,
  onComplete,
}) => {
  const widthScale = imageWidth > 0 ? stageWidth / imageWidth : 1;
  const heightScale = imageHeight > 0 ? stageHeight / imageHeight : 1;
  const normalizedTotalDuration = Math.max(0, Number(totalDuration) || 0);
  const normalizedStartTime = Math.max(0, Number(startTime) || 0);
  const animationObject = buildHitAreaModalAnimationObject(hitAreas);
  const hitAreaIds = Object.keys(animationObject);

  const timeline = gsap.timeline({
    paused: true,
    onUpdate: () => {
      if (typeof onTimeUpdate === "function") {
        onTimeUpdate(roundTimelineTime(timeline.time()));
      }
    },
    onComplete: () => {
      if (typeof onComplete === "function") {
        onComplete();
      }
    },
  });

  hitAreaIds.forEach((hitAreaId) => {
    const element = document.getElementById(
      getHitAreaModalPreviewElementId(hitAreaId)
    );
    if (!element) return;

    const entries = animationObject[hitAreaId]?.animation || [];
    if (!entries.length) return;

    gsap.set(element, {
      position: "absolute",
      ...toDisplayTweenVars(entries[0], widthScale, heightScale),
    });

    entries.slice(1).forEach((entry) => {
      timeline.to(
        element,
        {
          ...toDisplayTweenVars(entry, widthScale, heightScale),
          duration: Math.max(0, entry.duration || 0),
          ease: toGsapEase(entry.ease),
        },
        entry.delay || 0
      );
    });
  });

  hitAreas.forEach((hitArea) => {
    if (!hitArea?.id || hitArea.hideBoolean || animationObject[hitArea.id]) return;

    const element = document.getElementById(
      getHitAreaModalPreviewElementId(hitArea.id)
    );
    if (!element) return;

    const timelineStart = Number(hitArea.timelineStart) || 0;
    const timelineEnd = Number(hitArea.timelineEnd) || normalizedTotalDuration;
    const isVisibleAtStart =
      normalizedStartTime >= timelineStart - 0.05 &&
      normalizedStartTime <= timelineEnd + 0.05;

    gsap.set(element, {
      position: "absolute",
      left: roundToInt((hitArea.x || 0) * widthScale),
      top: roundToInt((hitArea.y || 0) * heightScale),
      width: Math.max(5, roundToInt((hitArea.width || 0) * widthScale)),
      height: Math.max(5, roundToInt((hitArea.height || 0) * heightScale)),
      opacity: isVisibleAtStart ? 1 : 0,
    });
  });

  if (normalizedTotalDuration > 0) {
    timeline.to({}, { duration: 0 }, normalizedTotalDuration);
  }

  timeline.time(normalizedStartTime);
  if (autoplay) {
    timeline.play();
  } else {
    timeline.pause();
  }

  return timeline;
};

export const scrubHitAreaModalTimeline = (timeline, timeInSeconds) => {
  if (!timeline) return;
  timeline.pause();
  timeline.time(Math.max(0, Number(timeInSeconds) || 0));
};

export const killHitAreaModalTimeline = (timeline, hitAreas = []) => {
  if (timeline) {
    timeline.kill();
  }
  resetHitAreaModalPreviewElements(hitAreas);
};
